import {FC, ReactNode} from 'react'

import {Text, TextFontWeight} from '@/components/common/content/Text'
import {ChevronIcon} from '@/components/common/icons/ChevronIcon'
import {CloseIcon} from '@/components/common/icons/CloseIcon'
import {LocalizedNumberFormat} from '@/components/common/LocalizedNumberFormat'
import {MIN_TARGET_SIZE} from '@/theme/sizing'
import {theme} from '@/theme/theme'
import {Currency} from '@/utils/currency'

type FormSummaryItemWithPriceProps = {
  title: string | ReactNode
  description?: string | ReactNode
  price?: number
  tooltip?: ReactNode
  fontWeight?: TextFontWeight
  textColor?: string
  isLowercase?: boolean
  hasDecimal?: boolean
  currency?: Currency | undefined
  onRemove?: () => void
  hasDropdownIcon?: boolean
  isBreakdownVisible?: boolean
  isPriceHighlighted?: boolean
}

export const FormSummaryItemWithPrice: FC<FormSummaryItemWithPriceProps> = ({
  title,
  description,
  price,
  tooltip,
  currency,
  fontWeight = TextFontWeight.regular,
  textColor = theme.palette.text.light,
  isLowercase = false,
  hasDecimal = true,
  onRemove,
  hasDropdownIcon,
  isBreakdownVisible,
  isPriceHighlighted = false,
}) => (
  <>
    <div className="row">
      <div className="title-wrapper">
        {onRemove && (
          <button className="icon close" type="button" onClick={onRemove}>
            <CloseIcon color={theme.palette.gray[600]} />
          </button>
        )}
        <Text color={textColor} fontWeight={fontWeight} className={isLowercase ? 'lowercase' : ''}>
          {title}
          {tooltip && <span className="tooltip">{tooltip}</span>}
        </Text>
        {hasDropdownIcon && (
          <div className="icon chevron">
            <ChevronIcon variant="down" color={textColor} isActive={isBreakdownVisible} />
          </div>
        )}
      </div>
      {price !== undefined ? (
        <div className="description price">
          <Text
            color={textColor}
            fontWeight={isPriceHighlighted ? TextFontWeight.bold : fontWeight}
            align="right"
          >
            <LocalizedNumberFormat
              value={price}
              type="currency"
              hasDecimal={hasDecimal}
              currency={currency}
            />
          </Text>
        </div>
      ) : (
        <div className="description">
          <Text
            color={textColor}
            fontWeight={isPriceHighlighted ? TextFontWeight.bold : fontWeight}
            className="wrap-anywhere"
            align="right"
          >
            {description}
          </Text>
        </div>
      )}
    </div>

    <style jsx>{`
      .row {
        display: flex;
        justify-content: space-between;
        width: 100%;
      }
      .title-wrapper {
        display: flex;
        align-items: flex-start;
      }
      .tooltip {
        display: inline-flex;
        position: relative;
        top: 2px;
        margin-left: ${theme.spacing[2]};
      }
      .price {
        white-space: nowrap;
      }
      .description {
        margin-left: ${theme.spacing[3]};
      }
      .icon {
        display: flex;
        align-items: center;
        justify-content: center;
        width: ${MIN_TARGET_SIZE}px;
        height: ${MIN_TARGET_SIZE}px;
        margin: 0 -${theme.spacing[0.5]};
      }
      .close {
        margin-right: ${theme.spacing[1.5]};
      }
      .chevron {
        margin-left: ${theme.spacing[1.5]};
      }
    `}</style>
  </>
)
